import { useCallback, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";

import ShieldCheckIcon from "@/assets/icons/shield-check.svg?react";
import TrendingDownIcon from "@/assets/icons/trending-down.svg?react";
import { BookingModal, ClientButton } from "@/components/clients";
import { clientPath, useClientBasePath } from "@/hooks/use-client-base-path";
import { useTciSession } from "@/hooks/use-tci-session";
import { useVisitorTracking } from "@/hooks/use-visitor-tracking";
import { getBookingScheduleUrl } from "@/lib/booking";
import {
  calculateCommercialInvestment,
  formatMoney,
  formatRoiPercent,
  isWorkflowId,
  parseNumericInput,
  toRegionalSpelling,
  type CommercialModel,
} from "@/lib/tci";
import { cn } from "@/lib/utils";

const INVESTMENT_WIDTH_PX = 892;

/**
 * CapEx / OpEx segment control (Figma 849:7649).
 * Selected: 45×24 fill + cyan border + TL/BR 6px L-brackets; idle: 50×22 plain label.
 */
function CommercialModelToggle({
  value,
  onChange,
}: {
  value: CommercialModel;
  onChange: (model: CommercialModel) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Commercial model"
      className="inline-flex w-fit shrink-0 items-center self-start bg-[#010b16] p-1.5"
    >
      <div className="flex h-6 items-center gap-1">
        {(["capex", "opex"] as const).map((model) => {
          const selected = value === model;
          const label = model === "capex" ? "CapEx" : "OpEx";

          return (
            <button
              key={model}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={label}
              onClick={() => onChange(model)}
              className={cn(
                "relative shrink-0 font-['Inter'] text-[10px] font-medium tracking-[-0.25px] text-white transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#38bdf8] focus-visible:ring-offset-2 focus-visible:ring-offset-black",
                selected
                  ? "flex h-6 w-[45px] flex-col items-stretch border border-[rgba(56,189,248,0.35)] bg-[rgba(56,189,248,0.35)] backdrop-blur-[8px]"
                  : "flex h-[22px] w-[50px] items-center justify-center bg-transparent hover:text-white/80",
              )}
            >
              {selected ? (
                <>
                  <span
                    aria-hidden
                    className="pointer-events-none absolute left-px top-px size-1.5 border-l border-t border-[#38bdf8]"
                  />
                  <span className="relative z-10 flex h-[22px] w-full items-center justify-center">
                    {label}
                  </span>
                  <span
                    aria-hidden
                    className="pointer-events-none absolute bottom-px right-px size-1.5 border-b border-r border-[#38bdf8]"
                  />
                </>
              ) : (
                label
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Investment & ROI screen (Figma CapEx/OpEx toggle 849:7649).
 * Opened from report results via "See Your Reclaimable ROI".
 */
export function ClientInvestmentRoiPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const basePath = useClientBasePath();
  const { session, result } = useTciSession();
  const { track } = useVisitorTracking();
  const [commercialModel, setCommercialModel] =
    useState<CommercialModel>("capex");
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const workflowParam = searchParams.get("workflow") ?? "";
  const workflowId = isWorkflowId(workflowParam) ? workflowParam : null;
  const workflowQuery = workflowId
    ? `?workflow=${encodeURIComponent(workflowId)}`
    : "";

  const regionId = session?.regionId ?? null;
  const headcount = parseNumericInput(session?.labor.headcount ?? "");
  const annualBleed = result?.totalTrapped ?? 0;

  const investment = useMemo(
    () => calculateCommercialInvestment(annualBleed, headcount, regionId),
    [annualBleed, headcount, regionId],
  );

  const bookingScheduleUrl = useMemo(
    () =>
      getBookingScheduleUrl({
        email: session?.email.trim() || undefined,
        workflowId: workflowId ?? undefined,
        tciScore: result?.tciScore ?? undefined,
        totalTrappedGbp:
          result != null
            ? formatMoney(result.totalTrapped, regionId)
            : undefined,
      }),
    [session?.email, workflowId, result, regionId],
  );

  const closeBooking = useCallback(() => setIsBookingOpen(false), []);
  const showBookingCta = basePath === "/client";

  if (!workflowId || !session || session.workflowId !== workflowId || !result) {
    return <Navigate to={basePath} replace />;
  }

  const roiPercent =
    commercialModel === "capex"
      ? investment.capexRoiPercent
      : investment.opexRoiPercent;

  return (
    <section
      className="mx-auto flex w-full flex-col items-start gap-10"
      style={{ maxWidth: INVESTMENT_WIDTH_PX }}
    >
      <div className="flex w-full flex-col items-start gap-10">
        <div className="flex w-full flex-col gap-4">
          <h1 className="max-w-[447px] font-['Inter'] text-[28px] font-normal leading-9 tracking-[-1.2px] text-white sm:text-4xl sm:leading-[44px] sm:tracking-[-1.8px] lg:text-[48.4px] lg:leading-[56.4px] lg:tracking-[-2.82px]">
            Investment & Return on Investment
          </h1>
          <p className="font-['Inter'] text-base font-medium leading-5 tracking-[-0.4px] text-[#9194a1]">
            Compare annual drag against Enbros AI workforce deployment
          </p>
        </div>

        <div className="flex w-full flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-6">
          {/* Annual Bleed */}
          <div className="flex min-h-[288px] w-full flex-col border border-[rgba(56,189,248,0.35)] backdrop-blur-[8px] lg:flex-1">
            <span
              aria-hidden
              className="size-4 shrink-0 border-l-2 border-t-2 border-[#38bdf8]"
            />
            <div className="flex flex-1 flex-col justify-between px-8 pb-2.5">
              <div className="flex flex-col items-start">
                <div className="flex items-center gap-1">
                  <TrendingDownIcon
                    aria-hidden
                    className="size-[14px] shrink-0"
                  />
                  <p className="font-['Inter'] text-[13px] font-normal uppercase leading-[22px] tracking-[-0.65px] text-[#ef4444]">
                    Annual Bleed
                  </p>
                </div>
                <p className="w-full font-['Inter'] text-[13px] font-normal uppercase leading-[22px] tracking-[-0.65px] text-[rgba(255,255,255,0.80)]">
                  The cost of humans performing robotic logic
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <p className="font-['Inter'] text-[36px] font-extrabold leading-none tracking-[-1.2px] text-[#ef4444] sm:text-[48px]">
                  {formatMoney(annualBleed, regionId)}
                </p>
                <p className="font-['Inter'] text-[13px] font-normal leading-[22px] tracking-[-0.65px] text-[rgba(255,255,255,0.80)]">
                  {toRegionalSpelling(
                    "Annualized Unrecovered Operating Loss",
                    regionId,
                  )}
                </p>
              </div>

              <div className="border-t border-[rgba(145,148,161,0.30)] py-1">
                <p className="font-['Inter'] text-[13px] font-normal leading-[22px] tracking-[-0.65px] text-[rgba(255,255,255,0.80)]">
                  Baseline:{" "}
                  <span className="text-white">Status Quo Manual Process</span>
                </p>
              </div>
            </div>
            <span className="flex w-full justify-end">
              <span
                aria-hidden
                className="size-4 shrink-0 border-b-2 border-r-2 border-[#38bdf8]"
              />
            </span>
          </div>

          {/* Enbros Solution */}
          <div className="flex min-h-[288px] w-full flex-col border border-[rgba(56,189,248,0.35)] backdrop-blur-[8px] lg:flex-1">
            <span
              aria-hidden
              className="size-4 shrink-0 border-l-2 border-t-2 border-[#38bdf8]"
            />
            <div className="flex flex-col px-8">
              <div className="flex items-center gap-1">
                <ShieldCheckIcon aria-hidden className="size-[14px] shrink-0" />
                <p className="font-['Inter'] text-[13px] font-normal uppercase leading-[22px] tracking-[-0.65px] text-[#38bdf8]">
                  The Enbros Solution
                </p>
              </div>
              <p className="font-['Inter'] text-[13px] font-normal leading-[22px] tracking-[-0.65px] text-white">
                <span className="text-[rgba(255,255,255,0.80)]">Arch:</span>
                &nbsp;{investment.tier.archLabel}
              </p>
            </div>

            <div className="flex flex-col gap-2.5 px-8 py-2.5">
              <CommercialModelToggle
                value={commercialModel}
                onChange={setCommercialModel}
              />

              {commercialModel === "capex" ? (
                <div className="flex flex-col gap-1">
                  <p className="font-['Inter'] text-[13px] font-normal uppercase leading-[22px] tracking-[-0.65px] text-[rgba(255,255,255,0.80)]">
                    One-off Build Investment
                  </p>
                  <p className="font-['Inter'] text-[36px] font-extrabold leading-none tracking-[-1.2px] text-white sm:text-[48px]">
                    {formatMoney(investment.baseBuildCost, regionId)}
                  </p>
                </div>
              ) : (
                <div className="flex w-full items-start justify-between gap-4">
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <p className="font-['Inter'] text-[13px] font-normal uppercase leading-[22px] tracking-[-0.65px] text-[rgba(255,255,255,0.80)]">
                      12-Month Plan
                    </p>
                    <p className="font-['Inter'] text-[0px] font-extrabold tracking-[-1.25px] text-white">
                      <span className="text-[36px] leading-none sm:text-[45px]">
                        {formatMoney(investment.monthly12, regionId)}
                      </span>
                      <span className="text-base leading-none text-[#9194a1]">
                        /mo
                      </span>
                    </p>
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col items-end gap-1">
                    <p className="font-['Inter'] text-[13px] font-normal uppercase leading-[22px] tracking-[-0.65px] text-[rgba(255,255,255,0.80)]">
                      24-Month Plan
                    </p>
                    <p className="font-['Inter'] text-[0px] font-extrabold tracking-[-1.25px] text-white">
                      <span className="text-[36px] leading-none sm:text-[45px]">
                        {formatMoney(investment.monthly24, regionId)}
                      </span>
                      <span className="text-base leading-none text-[#9194a1]">
                        /mo
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-auto flex flex-col px-8 pb-1">
              <div className="flex items-center justify-between font-['Inter'] text-xs font-normal tracking-[-0.6px] text-[rgba(255,255,255,0.80)]">
                <p className="leading-[22px]">Year 1 Net Savings</p>
                <p className="uppercase leading-[22px]">roi</p>
              </div>
              <div className="flex items-center justify-between text-base tracking-[-0.8px] text-[#22c55e]">
                <p className="font-['Inter'] font-bold leading-[22px]">
                  {formatMoney(investment.year1NetSavings, regionId)}
                </p>
                <p className="font-['Inter'] font-medium leading-[22px]">
                  {formatRoiPercent(roiPercent)}
                </p>
              </div>
            </div>

            <span className="flex w-full justify-end">
              <span
                aria-hidden
                className="size-4 shrink-0 border-b-2 border-r-2 border-[#38bdf8]"
              />
            </span>
          </div>
        </div>
      </div>

      <div className="flex h-auto w-full flex-col gap-2 sm:h-[46px] sm:flex-row sm:items-center sm:gap-2">
        <ClientButton
          variant="secondary"
          type="button"
          onClick={() =>
            navigate(
              `${clientPath(basePath, "calculator/results")}${workflowQuery}`,
            )
          }
        >
          Back
        </ClientButton>
        {showBookingCta ? (
          <div className="flex min-w-0 flex-1 sm:justify-end">
            <ClientButton
              variant="primary"
              type="button"
              disabled={!bookingScheduleUrl}
              onClick={() => {
                track({
                  currentStep: "booking",
                  lastEvent: "booking_opened",
                  workflowId,
                });
                setIsBookingOpen(true);
              }}
            >
              Book a Session
            </ClientButton>
          </div>
        ) : null}
      </div>

      {showBookingCta ? (
        <BookingModal
          open={isBookingOpen}
          scheduleUrl={bookingScheduleUrl}
          onClose={closeBooking}
        />
      ) : null}
    </section>
  );
}
